import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, CheckCircle2, Target, BookOpen, Brain, Shield, Loader2, Heart } from 'lucide-react';
import { PageTransition, FadeInItem } from '@/components/motion/Animations';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PHASES = [
  {
    num: 1,
    title: 'Admissão e Aceitação',
    days: 'Dias 1–30',
    icon: Heart,
    color: 'text-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    topics: [
      'Bem-vindo à sua jornada',
      'Entendendo a impotência',
      'A crença "eu paro quando quiser"',
      'A escada rolante que desce',
      'Vida é movimento',
      'As crenças que nos prendem',
      'Revisões semanais',
      'Adicção vs. Dicção',
      'Alexitimia — A incapacidade de expressar',
      'A parede de pregos',
      'Síndrome de abstinência aguda',
      'O perigo da "só uma vez"',
      'Sanidade e higiene mental',
      'Autoestima na recuperação',
      'Codependência e limites saudáveis',
      'Avaliação do primeiro mês',
    ],
  },
  {
    num: 2,
    title: 'Compreensão e Sanidade',
    days: 'Dias 31–60',
    icon: Brain,
    color: 'text-amber-500',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    topics: [
      'Conflitos emocionais infantis',
      'A criança interior',
      'Feridas do amor',
      'Transformando crenças limitantes',
      'Espiritualidade vs. materialidade',
      'Consolidação emocional',
    ],
  },
  {
    num: 3,
    title: 'Transformação e Prática',
    days: 'Dias 61–90',
    icon: Target,
    color: 'text-emerald-500',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    topics: [
      'Identificando e lidando com gatilhos',
      'Ferramentas práticas diárias',
      'Construindo rede de apoio',
      'Plano de prevenção de recaída',
      'Vivendo os princípios',
      'Planejando o futuro',
      'Celebração e compromisso contínuo',
    ],
  },
];

export default function Programa90Page() {
  const navigate = useNavigate();
  const [registering, setRegistering] = useState(false);
  const [registered, setRegistered] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/api/programs/interest/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.registered_programs?.includes('90-dias')) {
        setRegistered(true);
      }
    } catch {}
  };

  const handleRegister = async () => {
    const token = localStorage.getItem('token');
    if (!token) { toast.error('Faça login para registrar interesse'); return; }
    setRegistering(true);
    try {
      const res = await axios.post(`${API}/api/programs/interest`, { program: '90-dias' }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRegistered(true);
      toast.success(res.data.already_registered ? 'Interesse já registrado!' : 'Interesse registrado com sucesso!');
    } catch { toast.error('Erro ao registrar interesse'); }
    finally { setRegistering(false); }
  };

  return (
    <PageTransition>
      <div className="max-w-3xl mx-auto" data-testid="programa-90-page">
        {/* Back */}
        <Button onClick={() => navigate('/programas')} variant="ghost" size="sm" className="mb-4 -ml-2 text-muted-foreground" data-testid="back-to-programas">
          <ArrowLeft className="h-4 w-4 mr-1" /> Programas
        </Button>

        {/* Hero */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-3">
            <Calendar className="h-7 w-7 text-blue-500" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-bold mb-1">Programa 90 Dias</h1>
          <p className="text-sm text-muted-foreground">Método Fundador com Questionários Diários</p>
          <Badge className="mt-2 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Método PAREI-AD</Badge>
        </div>

        {/* Description */}
        <FadeInItem index={0}>
          <Card className="rounded-2xl border-border/40 shadow-sm mb-4">
            <CardContent className="p-5">
              <h2 className="font-bold text-sm mb-2 flex items-center gap-2"><BookOpen className="h-4 w-4 text-blue-500" /> Sobre o Programa</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                O Programa de 90 Dias da U-Clean é uma jornada estruturada de recuperação baseada no Método PAREI-AD (Enantiodromia), focada em estabelecer rotina, abstinência sustentada, gerenciamento de gatilhos e fissuras, e prevenção de recaída.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                A recuperação acontece <strong>um dia de cada vez</strong>. Como subir uma escada rolante que desce: requer esforço constante e consciente. Você não precisa ter todas as respostas — só precisa estar presente e disposto a tentar.
              </p>
            </CardContent>
          </Card>
        </FadeInItem>

        {/* How it works */}
        <FadeInItem index={1}>
          <Card className="rounded-2xl border-border/40 shadow-sm mb-4">
            <CardContent className="p-5">
              <h2 className="font-bold text-sm mb-3 flex items-center gap-2"><Shield className="h-4 w-4 text-blue-500" /> Como Funciona</h2>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" /> Conecte-se à plataforma U-Clean todos os dias</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" /> Complete a atividade prática do dia</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" /> Responda o questionário de 5 perguntas (2 pontos cada = 10 pts/dia)</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" /> Meta dos 90 dias: 720 pontos (80% de acertos)</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" /> Check-ins diários com a ARIA para acompanhamento emocional</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" /> Relatórios diários e semanais de progresso</li>
              </ul>
            </CardContent>
          </Card>
        </FadeInItem>

        {/* Phases */}
        <h2 className="font-bold text-base mb-3 mt-6">Estrutura do Programa</h2>
        <div className="space-y-3 mb-6">
          {PHASES.map((phase, i) => {
            const Icon = phase.icon;
            return (
              <FadeInItem key={phase.num} index={i + 2}>
                <Card className="rounded-2xl border-border/40 shadow-sm" data-testid={`phase-${phase.num}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-xl ${phase.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`h-5 w-5 ${phase.color}`} />
                      </div>
                      <div>
                        <h3 className="font-bold text-sm">Fase {phase.num}: {phase.title}</h3>
                        <p className="text-xs text-muted-foreground">{phase.days}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {phase.topics.map((t, j) => (
                        <Badge key={j} variant="outline" className="text-[10px] font-normal">{t}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </FadeInItem>
            );
          })}
        </div>

        {/* Includes */}
        <FadeInItem index={5}>
          <Card className="rounded-2xl border-border/40 shadow-sm mb-6">
            <CardContent className="p-5">
              <h2 className="font-bold text-sm mb-3">O que inclui</h2>
              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> 90 atividades diárias</div>
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> 450 perguntas com explicação</div>
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> Check-ins diários</div>
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> Acesso completo à ARIA</div>
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> 3 tarefas personalizadas/dia</div>
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> Plano de prevenção de recaída</div>
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> Relatórios de progresso</div>
                <div className="flex gap-1.5 items-start"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> Economia AWA (gamificação)</div>
              </div>
            </CardContent>
          </Card>
        </FadeInItem>

        {/* CTA */}
        <FadeInItem index={6}>
          <div className="text-center pb-8">
            {registered ? (
              <div className="space-y-2" data-testid="interest-registered">
                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-sm px-4 py-1.5">
                  <CheckCircle2 className="h-4 w-4 mr-1.5" /> Interesse registrado
                </Badge>
                <p className="text-xs text-muted-foreground">Você será notificado quando o programa estiver disponível para início.</p>
              </div>
            ) : (
              <>
                <Button
                  onClick={handleRegister}
                  disabled={registering}
                  className="bg-blue-600 hover:bg-blue-700 rounded-xl px-6"
                  data-testid="register-interest-90"
                >
                  {registering ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Quero participar
                </Button>
                <p className="text-xs text-muted-foreground mt-2">Sem cobrança. Registre seu interesse para ser notificado.</p>
              </>
            )}
          </div>
        </FadeInItem>
      </div>
    </PageTransition>
  );
}
